import { useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { useThemeCtx } from '../context/ThemeContext'

const NAV_LINKS = [
  { to: '/',          label: 'Üzleti lehetőség' },
  { to: '/rolam',     label: 'Rólam' },
]

const PRODUCTS_DROPDOWN = [
  { to: '/5in1', label: '✨ 5in1 Beauty & Men\'s Shot' },
]

export default function Header() {
  const [menuOpen,    setMenuOpen]    = useState(false)
  const [prodOpen,    setProdOpen]    = useState(false)  // mobilon
  const { theme, toggle } = useThemeCtx()
  const location = useLocation()

  const prodActive = PRODUCTS_DROPDOWN.some(p => location.pathname.startsWith(p.to))

  const linkClass = ({ isActive }) =>
    isActive
      ? 'text-gold-light font-semibold'
      : 'text-muted-text hover:text-heading transition-colors duration-200'

  return (
    <header className="sticky top-0 z-50 bg-surface/90 backdrop-blur-md border-b border-border/6"
            style={{ transition: 'background-color 0.25s ease' }}>
      <div className="max-w-6xl mx-auto px-6 flex items-center justify-between h-16">

        {/* Logo */}
        <NavLink to="/" className="font-display text-xl text-heading tracking-widest flex-shrink-0">
          Rose <span className="text-orange-500">Gold Team</span>
        </NavLink>

        {/* Desktop nav */}
        <nav className="hidden sm:flex items-center gap-7 text-sm font-medium">

          {NAV_LINKS.map(({ to, label }) => (
            <NavLink key={to} to={to} end className={linkClass}>
              {label}
            </NavLink>
          ))}

          {/* Termékek dropdown */}
          <div className="relative group">
            <button
              className={`flex items-center gap-1 transition-colors duration-200 ${
                prodActive ? 'text-gold-light font-semibold' : 'text-muted-text hover:text-heading'
              }`}
            >
              Termékek
              <ChevronIcon />
            </button>
            {/* Dropdown panel */}
            <div className="absolute top-full left-1/2 -translate-x-1/2 pt-3 hidden group-hover:block z-50">
              <div className="rounded-xl py-1.5 min-w-[200px]"
                   style={{
                     background: 'rgb(var(--surface-alt))',
                     border: '1px solid rgb(var(--border) / 0.18)',
                     boxShadow: '0 12px 40px rgba(0,0,0,0.45)',
                   }}>
                {PRODUCTS_DROPDOWN.map(p => (
                  <NavLink
                    key={p.to}
                    to={p.to}
                    className={({ isActive }) =>
                      `block px-4 py-2.5 text-sm transition-colors ${
                        isActive ? 'text-gold-light font-semibold bg-gold/5' : 'text-muted-text hover:text-heading hover:bg-border/5'
                      }`
                    }
                  >
                    {p.label}
                  </NavLink>
                ))}
              </div>
            </div>
          </div>

          <NavLink to="/kapcsolat" className={linkClass}>
            Kapcsolat
          </NavLink>

        </nav>

        {/* Desktop: téma toggle + CTA */}
        <div className="hidden sm:flex items-center gap-3">
          <ThemeToggle theme={theme} toggle={toggle} />
          <NavLink
            to="/kapcsolat"
            className="text-xs font-bold px-5 py-2 rounded-full text-ink transition-all hover:-translate-y-0.5"
            style={{ background: 'linear-gradient(90deg,#C9A84C,#F5EDD0)' }}
          >
            STRATÉGIAI HÍVÁS
          </NavLink>
        </div>

        {/* Mobile: toggle + hamburger */}
        <div className="sm:hidden flex items-center gap-2">
          <ThemeToggle theme={theme} toggle={toggle} />
          <button
            className="text-muted-text hover:text-heading p-1"
            onClick={() => setMenuOpen(o => !o)}
            aria-label="Menü"
          >
            {menuOpen ? '✕' : '☰'}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <nav className="sm:hidden flex flex-col px-6 pb-4 bg-surface-alt border-b border-border/5">
          {NAV_LINKS.map(({ to, label }) => (
            <NavLink
              key={to}
              to={to}
              end
              className={({ isActive }) =>
                `py-2.5 text-sm font-medium border-b border-border/5 ${
                  isActive ? 'text-gold-light' : 'text-muted-text'
                }`
              }
              onClick={() => setMenuOpen(false)}
            >
              {label}
            </NavLink>
          ))}

          {/* Termékek mobilon */}
          <button
            onClick={() => setProdOpen(o => !o)}
            className={`py-2.5 text-sm font-medium border-b border-border/5 text-left flex items-center justify-between ${
              prodActive ? 'text-gold-light' : 'text-muted-text'
            }`}
          >
            Termékek
            <span className={`transition-transform ${prodOpen ? 'rotate-180' : ''}`}>
              <ChevronIcon />
            </span>
          </button>
          {prodOpen && (
            <div className="pl-4 pb-1">
              {PRODUCTS_DROPDOWN.map(p => (
                <NavLink
                  key={p.to}
                  to={p.to}
                  className={({ isActive }) =>
                    `block py-2 text-sm border-b border-border/5 last:border-0 ${
                      isActive ? 'text-gold-light font-semibold' : 'text-muted-text'
                    }`
                  }
                  onClick={() => { setMenuOpen(false); setProdOpen(false) }}
                >
                  {p.label}
                </NavLink>
              ))}
            </div>
          )}

          <NavLink
            to="/kapcsolat"
            className={({ isActive }) =>
              `py-2.5 text-sm font-medium ${isActive ? 'text-gold-light' : 'text-muted-text'}`
            }
            onClick={() => setMenuOpen(false)}
          >
            Kapcsolat
          </NavLink>
        </nav>
      )}
    </header>
  )
}

function ChevronIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="2,4 6,8 10,4" />
    </svg>
  )
}

function ThemeToggle({ theme, toggle }) {
  return (
    <button
      onClick={toggle}
      aria-label={theme === 'dark' ? 'Világos mód bekapcsolása' : 'Sötét mód bekapcsolása'}
      className="w-8 h-8 rounded-full flex items-center justify-center text-muted-text hover:text-heading hover:bg-border/8 transition-all"
    >
      {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
    </button>
  )
}

function SunIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="5" />
      <line x1="12" y1="1"  x2="12" y2="3"  />
      <line x1="12" y1="21" x2="12" y2="23" />
      <line x1="4.22" y1="4.22"   x2="5.64"  y2="5.64"  />
      <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
      <line x1="1"  y1="12" x2="3"  y2="12" />
      <line x1="21" y1="12" x2="23" y2="12" />
      <line x1="4.22"  y1="19.78" x2="5.64"  y2="18.36" />
      <line x1="18.36" y1="5.64"  x2="19.78" y2="4.22"  />
    </svg>
  )
}

function MoonIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  )
}
